<template>
    <div class="mt-5">
	    <!-- Question -->
	    <div v-if="data.meta" class="d-flex flex-column gap-3 text-center mb-5">
		    <h3 v-if="questionDescription" class="fw-semi-bold m-0" v-html="questionDescription"></h3>
		    <p v-if="questionSubText" class="text-secondary m-0" v-html="questionSubText"></p>
	    </div>

        <!-- Input -->
        <div class="d-flex flex-column gap-3 mb-3" :class="scale.type" v-for="scale in scales">
            <p class="fw-semi-bold m-0">{{ scale.label }}</p>
            <div class="d-flex justify-content-between flex-nowrap gap-xs">
                <button class="btn btn-outline-secondary rounded-circle scale-button fw-semibold ripple"
                        :class="{ 'selected': isSelectedOption(scale, (i-1)) }" v-for="i in 11" :key="scale.label + '_' +(i-1)"
                        @click="selectScaleValue(scale, (i-1))" v-ripple>{{ i - 1 }}</button>
            </div>
            <div class="d-flex justify-content-between">
                <p class="text-secondary fs-sm-xs mb-0 w-25">{{ scale.minLabel }}</p>
                <p class="text-secondary fs-sm-xs mb-0 w-25">{{ scale.maxLabel }}</p>
            </div>

            <!-- Error message -->
            <p v-if="!data.skippable" class="text-danger my-3 user-select-none" :class="{ 'text-white': isScaleHasSelection(scale) }">Please select a value</p>
        </div>
    </div>
</template>

<script setup>
import { ref, watch } from "vue";

const props = defineProps({
	questionDescription: String,
	questionSubText: String,
    data: Object,
	answer: {
		type: Array,
		default: () => [],
	}
});

const scales = ref(props.data.scales);
const selectedOptions = ref(props.answer);

const emit = defineEmits(['question-answered']);
const selectScaleValue = (scale, value) => {
	if (selectedOptions.value.some(o => o.id === scale.id)) {
		const index = selectedOptions.value.findIndex(o => o.id === scale.id);
		selectedOptions.value[index].value = value;
		selectedOptions.value[index].text = value;
	} else {
		selectedOptions.value.push({ id: scale.id, value: value, text: value });
	}

	let allScalesHasSelection = false;
	for(let i = 0; i < scales.value.length; i++) {
		if (selectedOptions.value.some(o => o.id === scales.value[i].id)) {
			allScalesHasSelection = true;
		} else {
			allScalesHasSelection = false;
			break;
		}
	}
	if (allScalesHasSelection) {
		emit('question-answered', selectedOptions.value);
	}
};

const isSelectedOption = (scale, value) => {
	const selectedOption = selectedOptions.value.find(o => o.id === scale.id);
	return selectedOption && selectedOption.value === value;
}

const isScaleHasSelection = (scale) => {
	return selectedOptions.value.some(o => o.id === scale.id);
}

</script>

<style scoped lang="scss">
$ratingColors: (
    1: #FF0000,
    2: #FF3300,
    3: #FF6600,
    4: #FF9900,
    5: #FFCC00,
    6: #FFFF00,
    7: #CCFF00,
    8: #99FF00,
    9: #66FF00,
    10: #4efa23,
    11: #33FF00
);
$severityColors: (
    1: #FFA500,
    2: #FF8C00,
    3: #FF7300,
    4: #FF5900,
    5: #FF4000,
    6: #FF2600,
    7: #FF0D00,
    8: #FF0000,
    9: #CC0000,
    10: #b30505,
    11: #990000
);

.gap-xs {
    gap: 0.15rem;
}

.scale-button {
    width: 1.7em;
    flex-shrink: 0;
    padding: 0.15em;
    flex-grow: 1;
    aspect-ratio: 1;
    font-size: 15px;
}

.fs-sm-xs {
    @media (max-width: 400px) {
        font-size: 0.9em;
    }
}

@for $i from 1 through 11 {

    .severity {
        .scale-button:nth-child(#{$i}) {

            &:hover,
            &.selected {
                background-color: map-get($severityColors, $i);
                border: 1px solid darken(map-get($severityColors, $i), 10%);
                color: if($i > 1, #fff, #000)
            }
        }
    }

    .rating {
        .scale-button:nth-child(#{$i}) {

            &:hover,
            &.selected {
                background-color: map-get($ratingColors, $i);
                border: 1px solid darken(map-get($ratingColors, $i), 10%);
                color: if($i > 4, #000, #fff)
            }
        }
    }

    @media(max-width:768px) {
        .scale-button {
            &:hover:not(.selected) {
                background-color: transparent !important;
                border: 1px solid #6c757d !important;
                color: #6c757d !important;
            }
        }
    }
}
</style>